import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import utils from "@utils";
import { PAY_RATE_TYPE } from "@constants";
import PositionSummaryForm from "./PositionSummaryForm";

const PositionSummary = props => {
	const { lang } = global;
	const {
		positionGroupCount,
		positionStartDate,
		startTime,
		endTime,
		onEdit,
		onDelete,
		allowEdit,
		updateForm,
		jobPositions,
		isJobEvent,
		editingJob,
		user,
		agencyPayCodes,
		agencyPTAEOCodes,
		rawAgencyPayCodes,
		activePositionIndex,
		sortOrder,
		validateSection,
		user: {
			Organization: { PayCodesEnabled, IsPayCodeMandatory },
		},
	} = props;

	const [position, setPosition] = useState({
		...(jobPositions.find(j => j.SortOrder === sortOrder) || {}),
	});

	const {
		_isValid,
		Title,
		OfficersNeeded,
		Rate,
		SupervisorPosition,
		PositionGroupId,
		RequestedOfficerRank,
		SortOrder,
		PayCodeId,
		PTAEOCodeId,
		IsHidden,
		FlexJob,
		FlexJobMinHours,
		positionRate,
	} = position;

	const [positionName, setPositionName] = useState(Title);
	const [officersNeeded, setOfficersNeeded] = useState(OfficersNeeded);
	const [hourlyRate, setHourlyRate] = useState(Rate || null);
	const [positionStartTime, setPositionStartTime] = useState(utils.convertTime12to24(startTime));
	const [positionEndTime, setPositionEndTime] = useState(utils.convertTime12to24(endTime));
	const [positionDate, setPositionDate] = useState(positionStartDate);
	const [editPosition, setEditPosition] = useState(false);
	const [editingReportingPosition, setEditingReportingPosition] = useState(false);
	const [payCodeId, setPayCodeId] = useState(PayCodeId || null);
	const [ptaeoCodeId, setPTAEOCodeId] = useState(PTAEOCodeId || null);
	const [positionRateEditing, setPositionRateEditing] = useState(positionRate);
	const [positionVisible, setPositionVisible] = useState(IsHidden || true);
	const [reportingName, setReportingName] = useState("");
	const [reportingOfficersNeeded, setReportingOfficersNeeded] = useState("");
	const [reportingRate, setReportingRate] = useState("");
	const [reportingStartTime, setReportingStartTime] = useState(isJobEvent ? "" : startTime);
	const [reportingEndTime, setReportingEndTime] = useState(isJobEvent ? "" : endTime);
	const [reportingPositionDate, setReportingPositionDate] = useState(
		isJobEvent ? "" : positionStartDate,
	);
	const [positionReportingRateEditing, setPositionReportingRateEditing] = useState(
		IsPayCodeMandatory && PayCodesEnabled ? PAY_RATE_TYPE.PAY_CODE : PAY_RATE_TYPE.HOURLY,
	);
	const [reportingPayCodeId, setReportingPayCodeId] = useState(PayCodeId || null);
	const [payCodesToSelect, setPayCodesToSelect] = useState(
		isJobEvent
			? utils.filterPayCodesByDateAndStatus(
					positionStartDate,
					rawAgencyPayCodes,
					positionStartTime,
				)
			: agencyPayCodes,
	);
	const [applyPTAEOcode, setApplyPTAEOCode] = useState(PTAEOCodeId != null && PTAEOCodeId !== "");
	const [payCodes, setPaycodes] = useState(utils.parsePaycodesToReactSelect(rawAgencyPayCodes));
	const [isPositionValid, setIsPositionValid] = useState(true);

	const updatePayCodesToSelect = _startDate => {
		const _payCodes = utils.filterPayCodesByDateAndStatus(
			_startDate,
			rawAgencyPayCodes,
			positionStartTime,
		);
		setPayCodesToSelect(_payCodes);

		return _payCodes;
	};

	const validateSelectedPayCode = (_startDate, isReportingPosition = false) => {
		const payCodesToSelectIds = updatePayCodesToSelect(_startDate).map(p => p.value);

		if (!reportingPayCodeId || !payCodeId) return;

		if (!payCodesToSelectIds.includes(isReportingPosition ? reportingPayCodeId : payCodeId)) {
			if (isReportingPosition) {
				setReportingPayCodeId("");
			} else {
				setPayCodeId("");
			}
			utils.notify({
				type: "error",
				message: lang.t(
					"The selected pay code is outside the start date range, please change it to a valid one",
				),
				timeOut: 1500,
			});
		}
	};

	const updateStates = updatedPosition => {
		setPositionName(updatedPosition.Title);
		setOfficersNeeded(updatedPosition.OfficersNeeded);
		setHourlyRate(utils.isPayRateValid(updatedPosition.Rate) ? updatedPosition.Rate : "");
		if (updatedPosition._startTime)
			setPositionStartTime(utils.convertTime12to24(updatedPosition._startTime));
		if (updatedPosition._endTime)
			setPositionEndTime(utils.convertTime12to24(updatedPosition._endTime));
		setPositionDate(updatedPosition.positionStartDate || positionStartDate);
		setPayCodeId(updatedPosition.PayCodeId || null);
		setPTAEOCodeId(updatedPosition.PTAEOCodeId || null);
		setPositionRateEditing(
			(!updatedPosition.Rate && updatedPosition.PayCodeId) || IsPayCodeMandatory
				? PAY_RATE_TYPE.PAY_CODE
				: PAY_RATE_TYPE.HOURLY,
		);
	};

	const updatePosition = () => {
		const updatedPosition = { ...(jobPositions.find(j => j.SortOrder === SortOrder) || {}) };
		setPosition(updatedPosition);
		setPayCodeId(updatedPosition.PayCodeId);
		setPTAEOCodeId(updatedPosition.PTAEOCodeId);
		setApplyPTAEOCode(
			updatedPosition.PTAEOCodeId != null && updatedPosition.PTAEOCodeId !== "",
		);
		updateStates(updatedPosition);
	};

	useEffect(() => {
		updatePosition();
	}, [jobPositions]);

	useEffect(() => {
		setPayCodesToSelect(
			isJobEvent
				? utils.filterPayCodesByDateAndStatus(
						positionStartDate,
						rawAgencyPayCodes,
						positionStartTime,
					)
				: agencyPayCodes,
		);
	}, [agencyPayCodes]);

	useEffect(() => {
		if (editingReportingPosition) {
			updatePayCodesToSelect(positionDate);
		}
	}, [editingReportingPosition]);

	useEffect(() => {
		if (editPosition) {
			updatePayCodesToSelect(positionDate);
		}
	}, [editPosition]);

	useEffect(() => {
		updatePayCodesToSelect(positionStartDate);
	}, [positionStartDate]);

	useEffect(() => {
		updatePayCodesToSelect(reportingPositionDate);
	}, [reportingPositionDate]);

	useEffect(() => {
		const _payCodes = utils.parsePaycodesToReactSelect(rawAgencyPayCodes);
		setPaycodes(_payCodes);
	}, [rawAgencyPayCodes]);

	const formatPositionTime = time => {
		let date = new Date(`1970-01-01 ${time}`);
		if (utils.isIOSDevice) {
			// Date creation in iOS requires slashes
			date = new Date(`1970/01/01 ${time}`);
		}

		return date.toLocaleString(undefined, {
			hour: "numeric",
			minute: "2-digit",
			hour12: user.UserPreferences !== "useMilitaryTime",
		});
	};

	useEffect(() => {
		updatePosition();
	}, [activePositionIndex]);

	const handleEditPosition = value => {
		setEditPosition(value);
		updatePosition();
	};

	const handleEditingReportingPosition = value => {
		setEditingReportingPosition(value);
		updatePosition();
	};

	const handlePositionDate = value => {
		setPositionDate(value);
		validateSelectedPayCode(value);
	};

	const handleReportingPositionDate = value => {
		setReportingPositionDate(value);
		validateSelectedPayCode(value, true);
	};

	const onChangeOfficersNeeded = e => {
		const { value } = e.target;
		if (value !== "" && !value.match(/^[0-9]\d*$/g)) return;
		setOfficersNeeded(value);
	};

	const onChangeReportingOfficersNeeded = e => {
		const { value } = e.target;
		if (value !== "" && !value.match(/^[0-9]\d*$/g)) return;
		setReportingOfficersNeeded(value);
	};

	const onChangeHourlyRate = e => {
		const { value } = e.target;
		if (value !== "" && !value.match(/^\d*\.?\d{0,2}$/g)) return;

		setHourlyRate(value);
	};

	const onChangeReportingHourlyRate = e => {
		const { value } = e.target;
		if (value !== "" && !value.match(/^\d*\.?\d{0,2}$/g)) return;

		setReportingRate(value);
	};

	const militaryTimeMask = value => {
		const chars = value.split("");
		const hours = [/[0-2]/, chars[0] === "2" ? /[0-3]/ : /[0-9]/];
		const minutes = [/[0-5]/, /[0-9]/];
		return hours.concat(":").concat(minutes);
	};

	const sortNewPositions = positions => {
		let newJobs = [...positions];
		const positionGroupMap = {};
		if (editingJob) {
			// Existing jobs with position groups return a GUID, which makes it difficult for us to sort with the current method
			// To work around this, we create a map of existing position group IDs and rename them to something easier to sort
			positions.forEach((job, index) => {
				if (job.PositionGroupId && !positionGroupMap[job.PositionGroupId]) {
					positionGroupMap[job.PositionGroupId] = `positionGroup-${index}`;
				}
			});
			newJobs = newJobs.map(job => ({
				...job,
				PositionGroupId: positionGroupMap[job.PositionGroupId],
			}));
		}
		newJobs.sort((a, b) => {
			const aPositionGroup = a.PositionGroupId
				? parseInt(a.PositionGroupId.replace("positionGroup-", ""), 10)
				: 0;
			const bPositionGroup = b.PositionGroupId
				? parseInt(b.PositionGroupId.replace("positionGroup-", ""), 10)
				: 0;
			// In order of priority, sort by PositionGroupId, then supervisor status
			return aPositionGroup - bPositionGroup || b.SupervisorPosition - a.SupervisorPosition;
		});
		if (editingJob) {
			// Again, if editing a job, we want to preserve the original data from the API. So, map back to the original
			// Position group IDs provided by the API
			const positionGroupEntries = Object.entries(positionGroupMap);
			newJobs = newJobs.map(job => {
				if (positionGroupEntries.some(([, value]) => value === job.PositionGroupId)) {
					const oldId = positionGroupEntries.find(
						([, value]) => value === job.PositionGroupId,
					)[0];
					return { ...job, PositionGroupId: oldId };
				}
				return job;
			});
		}
		return newJobs;
	};

	const duplicatePosition = () => {
		const newJob = { ...(jobPositions.find(j => j.SortOrder === SortOrder) || {}) };
		delete newJob.Id;
		const maxSortOrder = Math.max(...jobPositions.map(j => j.SortOrder));
		newJob.SortOrder = maxSortOrder + 1;
		if (newJob.SupervisorPosition) newJob.PositionGroupId = `positionGroup-${Date.now()}`;
		updateForm("positions", { Jobs: sortNewPositions([...jobPositions, { ...newJob }]) });
	};

	const validatePosition = () => {
		if (
			isJobEvent &&
			(positionStartTime === "" || positionEndTime === "" || positionDate === "")
		)
			return false;
		if (
			(utils.isIE || utils.isDesktopSafari) &&
			isJobEvent &&
			(positionStartTime.replace(/_/g, "").length < 5 ||
				positionEndTime.replace(/_/g, "").length < 5)
		)
			return false;

		switch (positionRateEditing) {
			case PAY_RATE_TYPE.HOURLY:
				if (!hourlyRate || hourlyRate.toString().trim() === "") return false;
				break;

			case PAY_RATE_TYPE.PAY_CODE:
				if (!payCodeId || payCodeId.toString().trim() === "") return false;
				break;

			default:
				break;
		}

		if (applyPTAEOcode && (ptaeoCodeId == null || ptaeoCodeId === "")) return false;

		return true;
	};

	const getUpdatedPosition = pos => {
		const newOfficersNeeded =
			officersNeeded && parseInt(officersNeeded, 10) > 0 ? officersNeeded : OfficersNeeded;
		return {
			...pos,
			Title: positionName || Title,
			Rate: positionRateEditing === PAY_RATE_TYPE.HOURLY ? hourlyRate : "",
			OfficersNeeded: newOfficersNeeded,
			_needsMultipleOfficers: parseInt(newOfficersNeeded, 10) > 1,
			_startTime: isJobEvent ? positionStartTime : startTime,
			_endTime: isJobEvent ? positionEndTime : endTime,
			_positionStartDate: isJobEvent ? positionDate : "",
			PayCodeId: positionRateEditing === PAY_RATE_TYPE.PAY_CODE ? payCodeId : "",
			PTAEOCodeId: applyPTAEOcode ? ptaeoCodeId : "",
			IsHidden: !positionVisible,
			_isValid: true,
			positionRate: positionRateEditing,
		};
	};

	/**
	 * Saves changes made to any positions inline + allows for creation of reporting job positions
	 * Position name, officers needed, hourly rate and job start/end time are the only fields available to edit
	 */
	const savePosition = () => {
		let newPositions = [...jobPositions];
		setIsPositionValid(false);

		const isValid = validatePosition();

		if (!isValid) return;

		newPositions = newPositions.map(pos => {
			if (pos.SortOrder === SortOrder) {
				return getUpdatedPosition(pos);
			}
			return pos;
		});
		handleEditPosition(false);
		setIsPositionValid(true);

		validateSection(
			"positions",
			newPositions.filter(item => item._isValid === false).length === 0,
		);
		updateForm("positions", { Jobs: newPositions });
	};

	const showHidePosition = () => {
		let newPositions = [...jobPositions];
		newPositions = newPositions.map(pos => {
			if (pos.SortOrder === SortOrder) {
				return {
					...pos,
					IsHidden: !positionVisible,
				};
			}
			return pos;
		});

		setPositionVisible(!positionVisible);
		updateForm("positions", { Jobs: newPositions });
	};

	const saveReportingPosition = () => {
		let newPositions = [...jobPositions];
		const newOfficersNeeded =
			reportingOfficersNeeded && parseInt(reportingOfficersNeeded, 10) > 0
				? reportingOfficersNeeded
				: OfficersNeeded;
		setIsPositionValid(false);

		if (isJobEvent && (reportingStartTime === "" || reportingEndTime === "")) return;
		if (
			(utils.isIE || utils.isDesktopSafari) &&
			isJobEvent &&
			(reportingStartTime.replace(/_/g, "").length < 5 ||
				reportingEndTime.replace(/_/g, "").length < 5)
		)
			return;

		const { PositionGroupId: reportingPositionGroup } = jobPositions.find(
			j => j.SortOrder === SortOrder,
		);

		// If reporting position must be re-ordered to fall under supervisor, push the other indexes
		const mustPushExistingJobs = newPositions.find(j => j.SortOrder > SortOrder);
		if (mustPushExistingJobs) {
			newPositions.forEach(j => {
				if (j.SortOrder > SortOrder) {
					// eslint-disable-next-line no-param-reassign
					j.SortOrder++;
				}
			});
		}

		// const maxSortOrder = Math.max(...jobPositions.map((j) => j.SortOrder));
		// Default object required for when creating reporting positions
		const newReportingPosition = {
			Title: reportingName || Title,
			Rate: reportingRate,
			PayCodeId: reportingPayCodeId,
			OfficersNeeded: newOfficersNeeded,
			PositionGroupId: reportingPositionGroup,
			SupervisorPosition: false,
			SortOrder: SortOrder + 1,
			RequestedOfficerRankId: "",
			OfficerRankRequired: false,
			RovingPatrol: "",
			VehicleRequired: false,
			DressTypeId: 1,
			DressTypeNote: "",
			OfficerRequirements: "",
			FlexJob: false,
			FlexJobMinHours: "",
			JobCertifications: [],
			RequiredJurisdictionAssets: [],
			VehicleTypes: [],
			_needsMultipleOfficers: parseInt(reportingOfficersNeeded, 10) > 1,
			_isRovingPatrol: false,
			_assetsRequired: false,
			_certificationRequired: false,
			_hasAssignedOfficers: false,
			_initialRate: "",
			_isRankRequired: false,
			_positionStartDate: isJobEvent ? reportingPositionDate : "",
			_startTime: isJobEvent ? reportingStartTime : startTime,
			_endTime: isJobEvent ? reportingEndTime : endTime,
			_isValid: true,
			positionRate: positionReportingRateEditing,
		};

		switch (positionReportingRateEditing) {
			case PAY_RATE_TYPE.HOURLY:
				if (!reportingRate || reportingRate.toString().trim() === "") return;
				newReportingPosition.PayCodeId = "";
				break;

			case PAY_RATE_TYPE.PAY_CODE:
				if (!reportingPayCodeId || reportingPayCodeId.toString().trim() === "") return;
				newReportingPosition.Rate = "";
				break;

			default:
				break;
		}

		newPositions = sortNewPositions([...jobPositions, newReportingPosition]);
		setReportingName("");
		setReportingOfficersNeeded("");
		setReportingRate("");
		setReportingPositionDate("");
		setReportingStartTime(isJobEvent ? "" : startTime);
		setReportingEndTime(isJobEvent ? "" : endTime);
		setEditingReportingPosition(false);
		setIsPositionValid(true);

		updateForm("positions", { Jobs: newPositions, _expanded: true }, true);
	};

	const onCancel = () => {
		const newOfficersNeeded =
			officersNeeded && parseInt(officersNeeded, 10) > 0 ? officersNeeded : OfficersNeeded;
		handleEditPosition(false);
		setPositionName(positionName || Title);
		setHourlyRate(hourlyRate || Rate);
		setPayCodeId(payCodeId || PayCodeId);
		setPTAEOCodeId(ptaeoCodeId || PTAEOCodeId);
		setOfficersNeeded(newOfficersNeeded);
		setPositionDate(positionStartDate);
		setPositionStartTime(isJobEvent ? positionStartTime : startTime);
		setPositionEndTime(isJobEvent ? positionEndTime : endTime);
	};

	const handlePositionReportingRateChange = value => {
		let newValue = value;
		newValue = parseInt(value, 10);
		setPositionReportingRateEditing(newValue);
		setPayCodeId("");
		setHourlyRate("");
		setPosition({
			...position,
			positionRate: value,
		});
	};

	const handleReportingSelectChange = (e, name) => {
		const { value } = e;
		switch (name) {
			case "PayCodeId":
				setReportingPayCodeId(value);
				break;
			case "setPositionReportingRateEditing":
				handlePositionReportingRateChange(value);
				break;
			default:
				break;
		}
	};

	const handlePositionRateChange = value => {
		let newValue = value;
		newValue = parseInt(value, 10);
		setPositionRateEditing(newValue);
		setPayCodeId("");
		setHourlyRate("");
		setPosition({
			...position,
			positionRate: value,
		});
	};

	const handleSelectChange = (e, name) => {
		const { value } = e;
		switch (name) {
			case "PayCodeId":
				setPayCodeId(value);
				break;
			case "PTAEOCodeId":
				setPTAEOCodeId(value);
				break;
			case "positionRateEditing":
				handlePositionRateChange(value);
				break;
			case "setPositionReportingRateEditing":
				handlePositionReportingRateChange(value);
				break;
			default:
				break;
		}
	};

	const handlePositionChanges = e => {
		const {
			target: { name, value },
		} = e;
		switch (name) {
			case "positionName":
				setPositionName(value);
				break;
			case "officersNeeded":
				onChangeOfficersNeeded(e);
				break;
			case "positionStartDate":
				handlePositionDate(value);
				break;
			case "positionStartTime":
				setPositionStartTime(value);
				break;
			case "positionEndTime":
				setPositionEndTime(value);
				break;
			case "officerRate":
				onChangeHourlyRate(e);
				break;
			default:
				break;
		}
	};

	const handleReportingPositionChanges = e => {
		const {
			target: { name, value },
		} = e;
		switch (name) {
			case "positionName":
				setReportingName(value);
				break;
			case "officersNeeded":
				onChangeReportingOfficersNeeded(e);
				break;
			case "positionStartDate":
				handleReportingPositionDate(value);
				break;
			case "positionStartTime":
				setReportingStartTime(value);
				break;
			case "positionEndTime":
				setReportingEndTime(value);
				break;
			case "officerRate":
				onChangeReportingHourlyRate(e);
				break;
			default:
				break;
		}
	};

	const handleOnClickPosition = e => {
		e.preventDefault();
		handleEditPosition(true);
	};

	const handleCheckboxChange = ({ target: { checked } }) => {
		setApplyPTAEOCode(checked);
	};

	return (
		<Choose>
			<When condition={allowEdit && editPosition}>
				<div className="add-position sticky">
					<PositionSummaryForm
						positionName={positionName}
						isPositionValid={isPositionValid}
						handlePositionChanges={handlePositionChanges}
						savePosition={savePosition}
						officersNeeded={officersNeeded}
						isJobEvent={isJobEvent}
						positionStartDate={positionDate}
						positionStartTime={positionStartTime}
						positionEndTime={positionEndTime}
						user={user}
						militaryTimeMask={militaryTimeMask}
						generalInfoStartTime={isJobEvent ? positionStartTime : startTime}
						generalInfoEndTime={isJobEvent ? positionEndTime : endTime}
						formatPositionTime={formatPositionTime}
						PayCodesEnabled={PayCodesEnabled}
						IsPayCodeMandatory={IsPayCodeMandatory}
						positionRate={positionRateEditing}
						officerRate={hourlyRate}
						PayCodeId={payCodeId}
						PTAEOCodeId={ptaeoCodeId}
						handleSelectChange={handleSelectChange}
						agencyPayCodes={payCodesToSelect}
						agencyPTAEOCodes={agencyPTAEOCodes}
						onCancel={onCancel}
						saveButtonText={lang.t("Save")}
						cancelButtonText={lang.t("Cancel")}
						sortOrder={SortOrder}
						positionRateRadioName="positionRateEditing"
						// Place the following logic to disable start date when edit a reporting position !!PositionGroupId && !SupervisorPosition
						isReportingPositon={!!PositionGroupId && !SupervisorPosition}
						handleCheckboxChange={handleCheckboxChange}
						applyPTAEOcode={applyPTAEOcode}
					/>
				</div>
			</When>
			<Otherwise>
				<div
					className={classNames("position-summary", {
						"invalid-position": !_isValid,
						"reporting-position": !SupervisorPosition && PositionGroupId,
					})}
					role="button"
					tabIndex="0"
					onClick={e => {
						handleOnClickPosition(e);
					}}
					onKeyPress={e => {
						if (e.key === "Enter") {
							handleOnClickPosition(e);
						}
					}}
				>
					<div className="row">
						<div className="col-xs-12 col-sm-2">
							<strong className="visible-xs">{lang.t("Position")}</strong>
							{Title}
							&nbsp;
						</div>
						<div className="col-xs-6 col-sm-1">
							<br className="visible-xs" />
							<strong className="visible-xs">{`# ${lang.t("Officers Needed")}`}</strong>
							{OfficersNeeded}
							&nbsp;
						</div>
						<Choose>
							<When condition={positionRateEditing === PAY_RATE_TYPE.HOURLY}>
								<div className="col-xs-6 col-sm-3">
									<br className="visible-xs" />
									<strong className="visible-xs">{lang.t("Rate")}</strong>
									{`$${Rate}/hr`}
									<If condition={PTAEOCodeId != null && PTAEOCodeId !== ""}>
										<div style={{ marginTop: "10px" }}>
											{
												utils.getSelectedPTAEOCode(
													agencyPTAEOCodes,
													PTAEOCodeId,
												).label
											}
										</div>
									</If>
									&nbsp;
								</div>
							</When>
							<Otherwise>
								<div
									className="col-xs-6 col-sm-3"
									title={utils.getSelectedPayCode(payCodes, PayCodeId).title}
								>
									<br className="visible-xs" />
									<strong className="visible-xs">{lang.t("Pay Code")}</strong>
									{utils.getSelectedPayCode(payCodes, PayCodeId).label}
									<If condition={PTAEOCodeId != null && PTAEOCodeId !== ""}>
										<div style={{ marginTop: "10px" }}>
											{
												utils.getSelectedPTAEOCode(
													agencyPTAEOCodes,
													PTAEOCodeId,
												).label
											}
										</div>
									</If>
									&nbsp;
								</div>
							</Otherwise>
						</Choose>
						<div className="col-xs-12 col-sm-3 keep-column-without-content">
							<If condition={isJobEvent || FlexJob}>
								<br className="visible-xs" />
								<Choose>
									<When condition={isJobEvent}>
										<strong className="visible-xs">{`${lang.t("Date")} & ${lang.t("Time")}`}</strong>
									</When>
									<Otherwise>
										<strong className="visible-xs">{lang.t("Time")}</strong>
									</Otherwise>
								</Choose>
								<If
									condition={
										startTime !== "" &&
										endTime !== "" &&
										(isJobEvent || FlexJob)
									}
								>
									<Choose>
										<When
											condition={
												utils.isDesktopSafari ||
												utils.isIE ||
												utils.isFireFox
											}
										>
											{`${startTime} - ${endTime}`}
										</When>
										<Otherwise>
											{`${startTime} - ${endTime}`}
											&nbsp;
										</Otherwise>
									</Choose>
									<If condition={FlexJob}>
										<br className="hidden-xs" />
										<span
											className="label label-info"
											title={`${FlexJobMinHours} ${lang.t("Hr Minimum")}`}
										>
											{lang.t("FLEX")}
										</span>
									</If>
									<br className="visible-xs" />
								</If>
							</If>
						</div>
						<div className="col-xs-12 col-sm-3 text-right">
							<br className="visible-xs" />
							<div className="hidden">
								<br className="visible-xs" />
								<button
									type="button"
									id="edit-position"
									className="btn btn-link"
									onClick={e => {
										e.stopPropagation();
										showHidePosition();
									}}
									onKeyPress={e => {
										if (e.key === "Enter") {
											e.stopPropagation();
										}
									}}
									title={lang.t(!positionVisible ? "Show" : "Hide")}
									data-test-id="jobs_edit-position"
								>
									<i
										className={classNames("glyphicon", {
											"glyphicon-eye-open visibility-icon-visible":
												positionVisible,
											"glyphicon-eye-close visibility-icon-hidden":
												!positionVisible,
										})}
									/>
									&nbsp;
									<span className="visible-xs-inline">
										{lang.t(!positionVisible ? "Show" : "Hide")}
									</span>
								</button>
							</div>
							<br className="visible-xs" />
							<button
								type="button"
								id="edit-position"
								className="btn btn-link"
								disabled={!allowEdit}
								onClick={e => {
									e.stopPropagation();
									onEdit();
								}}
								onKeyPress={e => {
									if (e.key === "Enter") {
										e.stopPropagation();
										onEdit();
									}
								}}
								title={lang.t("Edit")}
								data-test-id="jobs_edit-position"
							>
								<i className="glyphicon glyphicon-pencil" />
								&nbsp;
								<span className="visible-xs-inline">{lang.t("Edit")}</span>
							</button>
							<button
								type="button"
								id="duplicate-position"
								data-test-id="jobs_duplicate-position"
								className="btn btn-link"
								onClick={e => {
									e.stopPropagation();
									duplicatePosition();
								}}
								onKeyPress={e => {
									if (e.key === "Enter") {
										e.stopPropagation();
										duplicatePosition();
									}
								}}
								title={lang.t("Duplicate")}
							>
								<i className="glyphicon glyphicon-duplicate" />
								&nbsp;
								<span className="visible-xs-inline">{lang.t("Duplicate")}</span>
							</button>
							<button
								type="button"
								id="delete-position"
								data-test-id="jobs_delete-position"
								className="btn btn-link"
								disabled={SupervisorPosition && positionGroupCount > 1}
								onClick={e => {
									e.stopPropagation();
									onDelete();
								}}
								title={lang.t("Delete")}
							>
								<i className="glyphicon glyphicon-trash text-danger" />
								&nbsp;
								<span className="visible-xs-inline">{lang.t("Delete")}</span>
							</button>
						</div>
					</div>
					<div className="row">
						<If condition={SupervisorPosition}>
							&nbsp; &nbsp; &nbsp;
							<span className="label label-primary">{lang.t("Supervisor")}</span>
							&nbsp;
						</If>

						<If condition={RequestedOfficerRank}>
							&nbsp;
							<span className="label label-info">{RequestedOfficerRank}</span>
							&nbsp;
						</If>
						<If condition={SupervisorPosition && !editingReportingPosition}>
							<div className="row">
								<div className="col-xs-12 add-reporting">
									<button
										type="button"
										className="btn btn-link"
										onClick={e => {
											e.stopPropagation();
											setReportingPositionDate(positionStartDate);
											handleEditingReportingPosition(true);
										}}
									>
										+ {lang.t("Add reporting position")}
									</button>
								</div>
							</div>
						</If>
					</div>
				</div>
				{/* Reporting Position Form */}
				<If condition={editingReportingPosition}>
					<div className="add-position sticky">
						<PositionSummaryForm
							positionName={reportingName}
							isPositionValid={isPositionValid}
							handlePositionChanges={handleReportingPositionChanges}
							savePosition={saveReportingPosition}
							officersNeeded={reportingOfficersNeeded}
							isJobEvent={isJobEvent}
							positionStartDate={reportingPositionDate}
							positionStartTime={reportingStartTime}
							positionEndTime={reportingEndTime}
							user={user}
							militaryTimeMask={militaryTimeMask}
							generalInfoStartTime={isJobEvent ? reportingStartTime : startTime}
							generalInfoEndTime={isJobEvent ? reportingEndTime : endTime}
							formatPositionTime={formatPositionTime}
							PayCodesEnabled={PayCodesEnabled}
							IsPayCodeMandatory={IsPayCodeMandatory}
							positionRate={positionReportingRateEditing}
							officerRate={reportingRate}
							PayCodeId={reportingPayCodeId}
							PTAEOCodeId={ptaeoCodeId}
							handleSelectChange={handleReportingSelectChange}
							agencyPayCodes={payCodesToSelect}
							agencyPTAEOCodes={agencyPTAEOCodes}
							onCancel={() => setEditingReportingPosition(false)}
							saveButtonText={`+ ${lang.t("Add")}`}
							cancelButtonText={lang.t("Cancel")}
							sortOrder={SortOrder}
							positionRateRadioName="setPositionReportingRateEditing"
							isReportingPositon="true"
							handleCheckboxChange={handleCheckboxChange}
							applyPTAEOcode={applyPTAEOcode}
						/>
					</div>
				</If>
			</Otherwise>
		</Choose>
	);
};

PositionSummary.propTypes = {
	onDelete: PropTypes.func.isRequired,
	onEdit: PropTypes.func.isRequired,
	allowEdit: PropTypes.bool,
	positionGroupCount: PropTypes.number,
	positionStartDate: PropTypes.string,
	startTime: PropTypes.string,
	endTime: PropTypes.string,
	updateForm: PropTypes.func,
	jobPositions: PropTypes.array,
	isJobEvent: PropTypes.bool,
	editingJob: PropTypes.bool,
	user: PropTypes.any.isRequired,
	agencyPayCodes: PropTypes.array,
	agencyPTAEOCodes: PropTypes.array,
	rawAgencyPayCodes: PropTypes.array,
	activePositionIndex: PropTypes.number,
	sortOrder: PropTypes.number.isRequired,
	validateSection: PropTypes.func,
};

PositionSummary.defaultProps = {
	allowEdit: true,
	positionGroupCount: 0,
	positionStartDate: "",
	startTime: "",
	endTime: "",
	jobPositions: [],
	isJobEvent: false,
	updateForm: () => {},
	editingJob: false,
	agencyPayCodes: [],
	agencyPTAEOCodes: [],
	rawAgencyPayCodes: [],
	activePositionIndex: null,
	validateSection: () => {},
};

export default PositionSummary;
